import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import { domQuerySelectorAll } from 'pf-frontend-common/src/helper/dom/query-selector-all';
import { UrlQueryParamsInterface } from 'pf-frontend-common/src/helper/url/query-params.interface';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { ViewBasicInterface } from 'pf-frontend-common/src/module/view/basic.interface';
import { ViewOptionsInterface } from 'pf-frontend-common/src/module/view/options.interface';
import { StatsContexterServiceInterface } from 'pf-frontend-common/src/service/stats-contexter/service.interface';
import { WindowLocationServiceInterface } from 'pf-frontend-common/src/service/window-location/service.interface';

import { i18nTranslate } from 'common/helper/i18n/translate';
import { AutocompleteResultInterface } from 'common/module/autocomplete/result.interface';
import { AutocompleteViewStoreEvent } from 'common/module/autocomplete/view-store.event';
import { AutocompleteViewStoreInterface } from 'common/module/autocomplete/view-store.interface';
import { BrokerAutocompleteStoreEvent } from 'common/module/broker/autocomplete/store.event';
import { BrokerAutocompleteStoreInterface } from 'common/module/broker/autocomplete/store.interface';
import { LoaderViewStore } from 'common/module/loader/view-store';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { BrokerHomeViewOptionsInterface } from 'desktop/module/broker/home/view-options.interface';
import { BrokerListViewStore } from 'desktop/module/broker/list/view-store';

export class BrokerHomeView implements ViewBasicInterface {
  /**
   * Main HTML element
   */
  protected element: HTMLElement;

  /**
   * Css selectors
   */
  protected cssSelector: DataKeyValueStringInterface = {
    switchSlider: '[data-qs=switch-slider]',
    switch: '[data-qs=switch]',
  };

  /**
   * Css classes
   */
  protected cssClass: DataKeyValueStringInterface = {
    switchSliderActive: 'switch_slider-sliding',
  };

  /**
   * Cached selected result from autocomplete
   */
  private selectedAutocompleteResult: AutocompleteResultInterface | null = null;

  /**
   * Constructor
   */
  constructor(
    private windowLocationService: WindowLocationServiceInterface,
    private autocompleteViewDynamic: ViewDynamicInterface<AutocompleteViewStoreInterface>,
    private loaderViewDynamic: ViewDynamicInterface<LoaderViewStore>,
    private brokerListViewDynamic: ViewDynamicInterface<BrokerListViewStore>,
    private brokerAutocompleteStore: BrokerAutocompleteStoreInterface,
    private statsContexterService: StatsContexterServiceInterface,
    private statsService: StatsServiceInterface
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: BrokerHomeViewOptionsInterface): void {
    // Keep HTML element
    this.element = options.element;

    // Initialize stats
    this.initStats();

    // Initialize subviews
    this.initSearchForm(options.subview.searchForm.viewOptions);
    this.initAutocomplete(options.subview.autocomplete.viewOptions);
    this.initBrokerList(options.subview.brokerList.viewOptions);
    this.initLoader(options.subview.loader.viewOptions);
    this.initSwitch();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    // Render subviews
    this.autocompleteViewDynamic.render();
    this.brokerListViewDynamic.render();

    // Log stats
    this.stats();
  }

  /**
   * Log stats for the page
   */
  private stats(): void {
    // Log stats
    this.statsService.pageView();
    this.statsService.brokerHome();
  }

  /**
   * Inits stats
   */
  private initStats(): void {
    this.statsContexterService.setBrokerHome(true);
  }

  /**
   * Inits search form
   */
  private initSearchForm(viewOptions: ViewOptionsInterface): void {
    viewOptions.element.addEventListener(DomElementEvent.submit, this.onSubmitSearchForm);
  }

  /**
   * Inits autocomplete view
   */
  private initAutocomplete(viewOptions: ViewOptionsInterface): void {
    this.autocompleteViewDynamic.initialize(viewOptions);

    const autocompleteViewStore = this.autocompleteViewDynamic.getViewStore();

    autocompleteViewStore.setPlaceholder(i18nTranslate('Enter a location or company name'));

    autocompleteViewStore.getEventEmitter().addListener(AutocompleteViewStoreEvent.keyUp, this.onKeyUpAutocomplete);

    autocompleteViewStore.getEventEmitter().addListener(AutocompleteViewStoreEvent.clear, this.onClearAutocomplete);

    autocompleteViewStore
      .getEventEmitter()
      .addListener(AutocompleteViewStoreEvent.selectResultItem, this.onSelectResultItemAutocomplete);

    this.brokerAutocompleteStore
      .getEventEmitter()
      .addListener(BrokerAutocompleteStoreEvent.searchSuccess, this.onSearchSuccessAutocompleteStore);
  }

  /**
   * Inits broker list view
   */
  private initBrokerList(viewOptions: ViewOptionsInterface): void {
    this.brokerListViewDynamic.initialize(viewOptions);
    this.brokerListViewDynamic.getViewStore().initialize();
  }

  /**
   * Init loader
   */
  private initLoader(viewOptions: ViewOptionsInterface): void {
    this.loaderViewDynamic.initialize(viewOptions);
    this.loaderViewDynamic.getViewStore().initialize();
  }

  /**
   * Init switch
   */
  private initSwitch(): void {
    const switchElements = domQuerySelectorAll(this.element, this.cssSelector.switch);

    Array.prototype.forEach.call(switchElements, (element: HTMLElement) => {
      element.addEventListener(DomElementEvent.click, this.onClickSwitch);
    });
  }

  /**
   * Autocomplete keyup event handler
   */
  private onKeyUpAutocomplete = () => {
    this.selectedAutocompleteResult = null;
    this.brokerAutocompleteStore.search(this.autocompleteViewDynamic.getViewStore().getState().searchString);
  };

  /**
   * Autocomplete clear event handler
   */
  private onClearAutocomplete = () => {
    this.selectedAutocompleteResult = null;
  };

  /**
   * Autocomplete select result item event handler
   *
   * @param e - Event
   * @param result - Autocomplete result
   */
  private onSelectResultItemAutocomplete = (e: Event, result: AutocompleteResultInterface) => {
    this.selectedAutocompleteResult = result;
  };

  /**
   * Triggers when autocomplete search succeed
   */
  private onSearchSuccessAutocompleteStore = () => {
    this.autocompleteViewDynamic
      .getViewStore()
      .setAutocompleteResults(this.brokerAutocompleteStore.getAutocompleteResults());
    const autocompleteState = this.autocompleteViewDynamic.getViewStore().getState();

    this.autocompleteViewDynamic
      .getViewStore()
      .toggle(!!autocompleteState.searchString && !!autocompleteState.autocompleteResults.length);
  };

  /**
   * Submitted search form
   *
   * @param e - Event
   */
  private onSubmitSearchForm = (e: Event) => {
    e.preventDefault();

    const target = <HTMLElement>e.target;
    const action = target.getAttribute('action');

    const params: UrlQueryParamsInterface = {};

    if (this.selectedAutocompleteResult) {
      params[this.selectedAutocompleteResult.sectionId] = this.selectedAutocompleteResult.id;
    }

    params.text = this.autocompleteViewDynamic.getViewStore().getState().searchString;

    // Redirect to SERP
    this.windowLocationService.redirect(action, {
      hash: '',
      queryData: params,
    });
  };

  /**
   * Clicked on switch
   */
  private onClickSwitch = (e: Event): void => {
    this.loaderViewDynamic.getViewStore().enable();

    domQuerySelector(this.element, this.cssSelector.switchSlider).classList.toggle(this.cssClass.switchSliderActive);
  };
}
